import { Link } from "react-router-dom";
import { HeartPulse, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

const UPDATED = "June 2026";

function P({ children }) {
  return <p className="text-[15px] text-slate-600 leading-relaxed">{children}</p>;
}
function UL({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-[15px] text-slate-600 leading-relaxed">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function LegalShell({ slug, title, intro, sections }) {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`legal-page-${slug}`}>
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#1E5AA8] flex items-center justify-center"><HeartPulse className="w-5 h-5 text-amber-300" /></div>
            <span className="font-display text-lg font-bold text-[#123C73]">MDvia Care</span>
          </Link>
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1E5AA8]">
            <ArrowLeft className="w-4 h-4" /> Back to app
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 py-10" data-notranslate>
        <p className="text-sm text-[#1E5AA8] font-semibold uppercase tracking-wide">Legal</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">{title}</h1>
        <p className="text-xs text-slate-400 mt-2">Last updated: {UPDATED}</p>
        {intro && <div className="mt-5">{intro}</div>}

        <nav className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/70 p-5" data-testid="legal-toc">
          <h2 className="text-sm font-semibold text-slate-700">Table of Contents</h2>
          <ol className="mt-3 space-y-1.5 text-sm list-decimal list-inside">
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`} className="text-[#1E5AA8] hover:underline">{s.h}</a></li>
            ))}
          </ol>
        </nav>

        <div className="mt-10 space-y-9">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="font-display text-xl font-bold text-slate-900">{s.h}</h2>
              <div className="mt-3 space-y-3">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-[#EBF3FB] border border-blue-100 p-5">
          <P>Questions about this policy? Contact us at <a href="mailto:support@mdviacare.com" className="text-[#1E5AA8] font-semibold">support@mdviacare.com</a>. MDvia Care is operated from India; correspondence address available on request.</P>
        </div>
      </main>

      <Footer />
    </div>
  );
}

const SERVICE_DESC = "MDvia Care is a Software-as-a-Service clinical documentation and productivity platform for licensed medical practitioners. It helps doctors record consultations, generate structured clinical notes and generic-only draft prescriptions, manage patient appointments and follow-ups, and produce printable OPD case sheets and medical certificates. MDvia Care is a tool that supports the treating doctor's own clinical judgement.";

export function Terms() {
  const sections = [
    { id: "acceptance", h: "1. Acceptance of Terms", body: (
      <P>By creating an account or using MDvia Care ("the Service", "we", "us"), operated at mdviacare.com, you ("the User", "you") agree to be bound by these Terms &amp; Conditions. If you do not agree, do not use the Service. These Terms form a legally binding agreement between you and MDvia Care.</P>
    )},
    { id: "service", h: "2. Description of Service", body: <P>{SERVICE_DESC}</P> },
    { id: "eligibility", h: "3. User Eligibility", body: (
      <UL items={[
        "Only licensed, registered medical practitioners (and staff explicitly authorised by such a doctor, e.g. a doctor's assistant) may register and use the Service.",
        "By registering you represent and warrant that you hold a valid, current medical registration/licence in your jurisdiction and that you are legally permitted to prescribe within your scope of practice.",
        "You are responsible for the accuracy of the professional details (name, qualifications, registration number, specialty) you provide.",
        "You must be at least 18 years old and legally capable of entering into this agreement.",
      ]} />
    )},
    { id: "doctor-responsibilities", h: "4. Doctor Responsibilities", body: (
      <>
        <P>MDvia Care is only a tool provider. It is not a medical advisor, healthcare provider, or party to the doctor–patient relationship.</P>
        <UL items={[
          "The treating doctor is solely and fully responsible for every prescription, diagnosis, dosage, clinical note, certificate and decision created or shared using the Service.",
          "AI-generated suggestions are drafts to assist you only. You must independently verify all medicines, doses, tests, and clinical content before relying on, printing, or sharing them.",
          "You are responsible for obtaining appropriate patient consent and for complying with all applicable medical, privacy, and record-keeping laws in your country.",
          "You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.",
        ]} />
      </>
    )},
    { id: "acceptable-use", h: "5. Acceptable Use", body: (
      <UL items={[
        "You may not use the Service for any unlawful, fraudulent, or misleading purpose, or to create false medical documentation.",
        "You may not attempt to reverse-engineer, resell, sublicense, or misuse the Service or its AI features.",
        "You may not upload malware or attempt to gain unauthorised access to the Service or other users' data.",
      ]} />
    )},
    { id: "subscription", h: "6. Subscription & Payments", body: (
      <UL items={[
        "Certain features are offered on a paid subscription basis. Subscriptions are billed monthly (or for the term you select) and, where applicable, renew automatically until cancelled.",
        "Subscriptions are personal to your account and are non-transferable.",
        "Payments are processed by third-party payment processors (such as Razorpay for India and Dodo Payments for international customers). Prices are shown in INR for India and in USD elsewhere, inclusive or exclusive of taxes as indicated at checkout.",
        "Refunds and cancellations are governed by our Refund & Cancellation Policy.",
      ]} />
    )},
    { id: "suspension", h: "7. Account Suspension & Termination", body: (
      <P>We may suspend or terminate your account, with or without notice, if we reasonably believe you have violated these Terms, misused the Service, engaged in fraudulent or unlawful activity, or created a risk or legal exposure for us or other users. You may stop using and close your account at any time.</P>
    )},
    { id: "ip", h: "8. Intellectual Property", body: (
      <P>All software, design, text, and branding of MDvia Care are owned by us or our licensors. Clinical content you create (patient records, prescriptions) remains yours and your patients'; you grant us a limited licence to process it solely to provide the Service.</P>
    )},
    { id: "third-party", h: "9. Third-Party Services", body: (
      <P>The Service integrates third-party services (e.g. authentication, AI models, and payment processors). Your use of those services may be subject to their own terms. We are not responsible for the acts or omissions of third-party providers.</P>
    )},
    { id: "liability", h: "10. Limitation of Liability", body: (
      <>
        <P>To the maximum extent permitted by law, MDvia Care is provided "as is" and "as available" without warranties of any kind. We do not warrant that AI suggestions are accurate, complete, or fit for any particular patient.</P>
        <P>We shall not be liable for any indirect, incidental, special, or consequential damages, or for any harm arising from clinical decisions, prescriptions, dosages, patient outcomes, or misuse of the Service. Our total aggregate liability shall not exceed the amount you paid to us in the three (3) months preceding the claim.</P>
      </>
    )},
    { id: "governing-law", h: "11. Governing Law & Dispute Resolution", body: (
      <P>MDvia Care is operated from India. For users, any dispute shall first be attempted to be resolved amicably, and failing that shall be settled by binding arbitration conducted in accordance with, and under the arbitration laws of, the user's own country of residence. Nothing in this clause limits any mandatory consumer-protection rights available to you under your local law.</P>
    )},
    { id: "changes", h: "12. Changes to These Terms", body: (
      <P>We may update these Terms from time to time. Material changes will be notified via the app or email. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.</P>
    )},
    { id: "contact", h: "13. Contact", body: (
      <P>For any questions regarding these Terms, contact <a href="mailto:support@mdviacare.com" className="text-[#1E5AA8] font-medium">support@mdviacare.com</a>.</P>
    )},
  ];
  return <LegalShell slug="terms" title="Terms & Conditions" intro={<P>Please read these Terms &amp; Conditions carefully before using MDvia Care.</P>} sections={sections} />;
}

export function Privacy() {
  const sections = [
    { id: "intro", h: "1. Introduction", body: (
      <P>This Privacy Policy explains how MDvia Care ("we", "us"), operating at mdviacare.com from India, collects, uses, stores, and protects personal data. We act as a data processor for patient information entered by doctors, and as a data controller for the doctor's own account information. We are committed to compliance with the EU/UK General Data Protection Regulation (GDPR) and applicable Indian data-protection law.</P>
    )},
    { id: "collect", h: "2. Information We Collect", body: (
      <UL items={[
        "Doctor/account data: name, email address, clinic name, clinic address, phone/WhatsApp number, professional qualifications, registration number, specialty, and profile picture (via Google sign-in).",
        "Patient data: patient name and phone/WhatsApp number and (optionally) email — collected only so the doctor can send that patient their prescription — plus the clinical details the doctor chooses to record for that patient.",
        "Usage data: basic technical logs (e.g. device/browser, timestamps) needed to operate and secure the Service.",
      ]} />
    )},
    { id: "use", h: "3. How We Use Data", body: (
      <UL items={[
        "To provide the Service: authentication, generating and storing clinical notes and prescriptions, appointments, and delivering prescriptions on the doctor's instruction.",
        "To secure, maintain, and improve the Service.",
        "To process subscription payments via our payment processors.",
        "We do NOT sell, rent, or share your or your patients' personal data with third parties for marketing.",
      ]} />
    )},
    { id: "legal-basis", h: "4. Legal Basis (GDPR)", body: (
      <P>Where GDPR applies, we process data on the basis of contract performance (providing the Service), legitimate interests (security and improvement), consent (where required), and legal obligations. Doctors are responsible for having a lawful basis and appropriate consent for the patient data they enter.</P>
    )},
    { id: "cookies", h: "5. Cookies", body: (
      <P>We use strictly necessary cookies and similar technologies (such as a secure session cookie) to keep you logged in and to operate the Service. We do not use advertising cookies. You can control cookies through your browser settings, but disabling essential cookies may prevent the Service from working.</P>
    )},
    { id: "security", h: "6. Data Security", body: (
      <P>Data is transmitted over encrypted connections (HTTPS/TLS) and stored in access-controlled databases. We apply administrative, technical, and organisational safeguards appropriate to the sensitivity of health data. No method of transmission or storage is 100% secure, but we work continuously to protect your information.</P>
    )},
    { id: "retention", h: "7. Data Retention", body: (
      <P>We retain account and clinical data for as long as your account is active or as needed to provide the Service and to meet legal, tax, or medical record-keeping requirements. You may request deletion as described below.</P>
    )},
    { id: "rights", h: "8. Your Rights", body: (
      <UL items={[
        "You have the right to access, correct, export, restrict, or delete your personal data, and to withdraw consent where processing is based on consent.",
        "To exercise any right — including deletion of your data — email support@mdviacare.com. We will respond within the timeframe required by applicable law.",
        "You also have the right to lodge a complaint with your local data-protection authority.",
      ]} />
    )},
    { id: "subprocessors", h: "9. Sub-processors & International Transfers", body: (
      <P>We use trusted sub-processors for hosting, authentication, AI processing, and payments. Where data is transferred internationally, we rely on appropriate safeguards (such as standard contractual clauses) as required by law.</P>
    )},
    { id: "children", h: "10. Children's Data", body: (
      <P>The Service is intended for use by licensed professionals, not by children. Patient records may relate to patients of any age, but such records are entered and controlled solely by the treating doctor under their own legal responsibility.</P>
    )},
    { id: "contact", h: "11. Contact", body: (
      <P>For privacy questions or data-deletion requests, contact <a href="mailto:support@mdviacare.com" className="text-[#1E5AA8] font-medium">support@mdviacare.com</a>.</P>
    )},
  ];
  return <LegalShell slug="privacy" title="Privacy Policy" intro={<P>Your privacy and your patients' privacy matter to us. This policy describes what we collect and how we protect it.</P>} sections={sections} />;
}

export function RefundPolicy() {
  const sections = [
    { id: "overview", h: "1. Overview", body: (
      <P>This Refund &amp; Cancellation Policy applies to all paid subscriptions to MDvia Care, purchased through our payment processors (Razorpay for India, Dodo Payments for international customers). Prices are charged in INR for customers in India and in USD for customers elsewhere.</P>
    )},
    { id: "renewal", h: "2. Auto-Renewal", body: (
      <P>Monthly subscriptions renew automatically at the end of each billing cycle using your selected payment method, until you cancel. Longer plans (e.g. quarterly or annual) renew at the end of their term unless cancelled.</P>
    )},
    { id: "cancel", h: "3. Cancellation", body: (
      <UL items={[
        "You may cancel your subscription at any time from your dashboard (Account / Billing) or by emailing support@mdviacare.com.",
        "On cancellation, your plan remains active until the end of the current paid period and then stops renewing.",
      ]} />
    )},
    { id: "refunds", h: "4. Refunds", body: (
      <UL items={[
        "Subscription fees for the current billing period are non-refundable once payment has been made.",
        "If you are charged twice for the same period due to a technical or billing error, you are entitled to a full refund of the duplicate charge — contact support@mdviacare.com within seven (7) days of the charge.",
        "Approved refunds are issued to the original payment method within the processor's standard timelines.",
      ]} />
    )},
    { id: "failed", h: "5. Failed Payments & Downgrade", body: (
      <P>If a renewal payment fails, we will attempt to notify you and retry. If payment is not completed within a grace period of three (3) days, your account will be downgraded to the free plan. Your data is retained and premium features are restored once payment succeeds.</P>
    )},
    { id: "chargebacks", h: "6. Chargebacks", body: (
      <P>If you believe a charge is incorrect, please contact us first so we can resolve it quickly. Initiating a chargeback without contacting us may result in suspension of your account pending resolution.</P>
    )},
    { id: "contact", h: "7. Contact", body: (
      <P>For billing, cancellation, or refund requests, contact <a href="mailto:support@mdviacare.com" className="text-[#1E5AA8] font-medium">support@mdviacare.com</a>.</P>
    )},
  ];
  return <LegalShell slug="refund" title="Refund & Cancellation Policy" intro={<P>This policy explains how subscriptions renew, how to cancel, and when refunds apply.</P>} sections={sections} />;
}

export function Disclaimer() {
  const sections = [
    { id: "nature", h: "1. Nature of the Service", body: <P>{SERVICE_DESC} MDvia Care is not a medical device, and it is not intended to diagnose, treat, cure, or prevent any disease.</P> },
    { id: "responsibility", h: "2. Doctor's Sole Responsibility", body: (
      <UL items={[
        "All medical decisions are the exclusive responsibility of the treating, licensed doctor.",
        "Doctors must independently verify every detail — patient identity, allergies, diagnosis, medicine, dose, frequency, duration, and interactions — before printing, signing, or sending any prescription or certificate.",
        "AI-generated content is a draft aid only and may contain errors or omissions.",
      ]} />
    )},
    { id: "no-liability", h: "3. No Liability for Outcomes", body: (
      <P>To the fullest extent permitted by law, MDvia Care and its operators are not liable for any incorrect dosage, drug interaction, misdiagnosis, misuse, data-entry error, or patient outcome arising from use of the Service. Reliance on any information produced by the Service is entirely at the treating doctor's own professional risk.</P>
    )},
    { id: "no-relationship", h: "4. No Doctor–Patient Relationship With Us", body: (
      <P>MDvia Care does not create a doctor–patient relationship between us and any patient. We do not provide medical advice to patients and are not a party to the care provided by the doctor.</P>
    )},
    { id: "emergency", h: "5. Not for Emergencies", body: (
      <P>The Service is not designed for medical emergencies. In an emergency, contact local emergency services or the nearest hospital immediately.</P>
    )},
    { id: "contact", h: "6. Contact", body: (
      <P>Questions about this disclaimer? Contact <a href="mailto:support@mdviacare.com" className="text-[#1E5AA8] font-medium">support@mdviacare.com</a>.</P>
    )},
  ];
  return <LegalShell slug="disclaimer" title="Medical Disclaimer" intro={<P>Please read this disclaimer carefully. It defines the limits of the Service and the responsibilities of the treating doctor.</P>} sections={sections} />;
}
